import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../lib/ThemeContext';
import { Modal } from '../ui/modal';
import { Button } from '../ui/button';
import { Upload, FileText, X } from 'lucide-react';
import { APP_PROPERTIES } from '../../config/appProperties';

interface LoadParticipantsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadParticipants: (participants: { id: string; name: string }[]) => void;
  currentParticipants: { id: string; name: string }[];
}

export const LoadParticipantsModal = ({
  isOpen,
  onClose,
  onLoadParticipants,
  currentParticipants
}: LoadParticipantsModalProps) => {
  const { t } = useTranslation();
  const { currentTheme } = useTheme();
  const [textInput, setTextInput] = useState('');
  const [error, setError] = useState('');
  const [loadMode, setLoadMode] = useState<'clear' | 'append'>('clear');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const charLimit = APP_PROPERTIES.PARTICIPANT_NAME_CHAR_LIMIT;
  const maxParticipants = APP_PROPERTIES.MAX_PARTICIPANTS;

  const parseParticipants = (input: string): { id: string; name: string }[] => {
    if (!input.trim()) {
      throw new Error(t('participant.load.emptyInput', 'Please provide participant names'));
    }

    // Split by comma, newline, or semicolon and clean up
    const names = input
      .split(/[,;\n]/)
      .map(name => name.trim())
      .filter(name => name.length > 0);

    if (names.length === 0) {
      throw new Error(t('participant.load.noValidNames', 'No valid participant names found'));
    }

    // Check for duplicate names within the input
    const uniqueNames = new Set<string>();
    const duplicates: string[] = [];
    
    names.forEach(name => {
      if (uniqueNames.has(name)) {
        duplicates.push(name);
      } else {
        uniqueNames.add(name);
      }
    });

    if (duplicates.length > 0) {
      throw new Error(t('participant.load.duplicates', `Duplicate names found: ${duplicates.join(', ')}`));
    }

    // If append mode, check for duplicates with existing participants
    if (loadMode === 'append') {
      const existingNames = new Set(currentParticipants.map(p => p.name));
      const conflicts: string[] = [];
      
      names.forEach(name => {
        if (existingNames.has(name)) {
          conflicts.push(name);
        }
      });

      if (conflicts.length > 0) {
        throw new Error(t('participant.load.conflicts', `Names already exist: ${conflicts.join(', ')}`));
      }

      // Check total count
      if (currentParticipants.length + names.length > maxParticipants) {
        throw new Error(t('participant.load.tooManyAppend', `Adding these names would exceed the maximum of ${maxParticipants} participants`));
      }
    } else {
      // Clear mode - check if new names exceed limit
      if (names.length > maxParticipants) {
        throw new Error(t('participant.load.tooMany', `Maximum ${maxParticipants} participants allowed`));
      }
    }

    // Create participant objects with unique IDs
    return names.map((name, index) => ({
      id: `${Date.now()}-${index}`,
      name: name.substring(0, charLimit)
    }));
  };

  const handleLoadFromText = () => {
    try {
      setError('');
      const newParticipants = parseParticipants(textInput);
      
      if (loadMode === 'append') {
        // Append new participants to existing ones
        const combinedParticipants = [...currentParticipants, ...newParticipants];
        onLoadParticipants(combinedParticipants);
      } else {
        // Clear and load new participants
        onLoadParticipants(newParticipants);
      }
      
      onClose();
      setTextInput('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        setError('');
        const content = e.target?.result as string;
        const newParticipants = parseParticipants(content);
        
        if (loadMode === 'append') {
          // Append new participants to existing ones
          const combinedParticipants = [...currentParticipants, ...newParticipants];
          onLoadParticipants(combinedParticipants);
        } else {
          // Clear and load new participants
          onLoadParticipants(newParticipants);
        }
        
        onClose();
        setTextInput('');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    };
    reader.readAsText(file);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('participant.load.title', 'Load Participants')}
    >
      <div className="space-y-4">
        {/* Load Mode Selection */}
        <div className="border-b pb-3" style={{ borderColor: currentTheme.border }}>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="loadMode"
                value="clear"
                checked={loadMode === 'clear'}
                onChange={() => setLoadMode('clear')}
                className="mr-2"
              />
              <span style={{ color: currentTheme.textSecondary }}>
                {t('participant.load.clearAndLoad', 'Clear and Load')}
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="loadMode"
                value="append"
                checked={loadMode === 'append'}
                onChange={() => setLoadMode('append')}
                className="mr-2"
              />
              <span style={{ color: currentTheme.textSecondary }}>
                {t('participant.load.append', 'Append')}
              </span>
            </label>
          </div>
        </div>

        {/* Browse CSV Section */}
        <div>
          <Button
            onClick={handleBrowseClick}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white"
            style={{ backgroundColor: '#0078d7' }}
          >
            <Upload className="h-4 w-4 mr-2" />
            {t('participant.load.browse', 'Browse CSV')}
          </Button>
        </div>

        {/* Divider */}
        <div className="flex items-center">
          <div className="flex-1 border-t" style={{ borderColor: currentTheme.border }}></div>
          <span className="px-3 text-sm text-gray-500">{t('participant.load.or', 'or')}</span>
          <div className="flex-1 border-t" style={{ borderColor: currentTheme.border }}></div>
        </div>

        {/* Text Input Section */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.textSecondary }}>
            {t('participant.load.enterNames', 'Enter participant names separated by commas')}
          </label>
          
          <textarea
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder={t('participant.load.placeholder', 'John, Jane, Bob, Alice...')}
            className="w-full p-3 border rounded-md resize-none mb-3"
            rows={4}
            style={{
              borderColor: currentTheme.border,
              color: currentTheme.textPrimary,
              backgroundColor: currentTheme.paper
            }}
          />

          <Button
            onClick={handleLoadFromText}
            className="w-full bg-green-500 hover:bg-green-600 text-white"
            disabled={!textInput.trim()}
          >
            <FileText className="h-4 w-4 mr-2" />
            {t('participant.load.loadFromText', 'Load from Text')}
          </Button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.txt"
          onChange={handleFileUpload}
          className="hidden"
        />

        {error && (
          <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
            <div className="flex items-center">
              <X className="h-4 w-4 mr-2" />
              {error}
            </div>
          </div>
        )}

        <div className="text-xs text-gray-500 text-center">
          Maximum {maxParticipants} participants, {charLimit} characters per name
        </div>
      </div>
    </Modal>
  );
}; 